import AddRounded from '@mui/icons-material/AddRounded';
import CategoryRounded from '@mui/icons-material/CategoryRounded';
import QrCodeScannerRounded from '@mui/icons-material/QrCodeScannerRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, SearchField, type Column } from '../../components/DataTable';
import { EmptyState, PageHeader } from '../../components/states';
import { BarcodeLookupDialog } from './BarcodeLookupDialog';
import { catalogKeys, EMPTY_SELECT_SLOTS, packBreakdown, type Product } from './model';
import { qty } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { useBrands, useCategories } from './queries';
import { ActiveChip, CategorySelect, PriceRangeText, ProductThumb } from './ui';
import { useExportSelection } from '../dataio/useExportSelection';
import { filesColumn, useAttachmentCounts } from '../attachments';

type ActiveFilter = 'true' | 'false' | 'all';
type Sort = 'name' | '-createdAt' | 'code';

/** /products — searchable catalog with category/brand filters and barcode lookup. */
export function ProductListPage() {
  const exportSelection = useExportSelection('product');
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const categoryId = params.get('category') ?? '';
  const brandId = params.get('brand') ?? '';
  const active = (params.get('active') ?? 'true') as ActiveFilter;
  const sort = (params.get('sort') ?? 'name') as Sort;
  const page = Number(params.get('page') ?? '1') || 1;
  const pageSize = Number(params.get('pageSize') ?? '25') || 25;
  const [scanning, setScanning] = useState(false);
  const categories = useCategories();
  const brands = useBrands();

  const update = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [key, v] of Object.entries(next)) {
      if (v === null || v === '') p.delete(key);
      else p.set(key, v);
    }
    setParams(p, { replace: true });
  };

  const query = {
    q: q || undefined,
    categoryId: categoryId || undefined,
    brandId: brandId || undefined,
    isActive: active === 'all' ? undefined : active,
    sort,
    page,
    pageSize,
  };
  const list = useQuery({
    queryKey: catalogKeys.productList(query),
    queryFn: () => unwrap(api.GET('/products', { params: { query } })),
    placeholderData: keepPreviousData,
  });

  const fileCounts = useAttachmentCounts(
    'PRODUCT',

    (list.data?.data ?? []).map((r) => r.id),
  );

  const columns: Column<Product>[] = [
    {
      key: 'name',
      header: 'Product',
      render: (p) => (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
          <ProductThumb productId={p.id} imageId={p.images[0]?.id} />
          <Box sx={{ minWidth: 0, maxWidth: { xs: 140, sm: 260, md: 'none' } }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {p.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                {p.code}
              </Box>
              {p.brand ? ` · ${p.brand.name}` : ''}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      hideBelow: 'md',
      render: (p) =>
        p.category ? (
          <Typography variant="body2" noWrap title={p.category.path}>
            {p.category.path}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        ),
    },
    {
      key: 'variants',
      header: 'Variants',
      hideBelow: 'lg',
      render: (p) => {
        const live = p.variants.filter((v) => v.isActive).length;
        return (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" className="tabular">
              {live}
            </Typography>
            {p.variants.length > 1 && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {p.variants
                  .filter((v) => v.isActive)
                  .slice(0, 2)
                  .map((v) => v.nameSuffix ?? v.sku)
                  .join(', ')}
                {live > 2 ? '…' : ''}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      key: 'unit',
      header: 'Unit',
      hideBelow: 'lg',
      render: (p) => (
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <Typography variant="body2">{p.baseUnit.symbol}</Typography>
          {!p.trackInventory && (
            <Chip
              size="small"
              label="Service"
              variant="outlined"
              sx={{ height: 20, fontSize: 11 }}
            />
          )}
        </Stack>
      ),
    },
    {
      key: 'onHand',
      header: 'On hand',
      align: 'right',
      hideBelow: 'sm',
      render: (p) =>
        p.stock ? (
          <Box>
            <Typography
              variant="body2"
              className="tabular"
              sx={{
                fontWeight: 600,
                color: Number(p.stock.onHand) <= 0 ? 'error.main' : undefined,
              }}
            >
              {qty(p.stock.onHand, format)} {p.baseUnit.symbol}
            </Typography>
            {packBreakdown(p.stock.onHand, p.baseUnit, p.units) && (
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {packBreakdown(p.stock.onHand, p.baseUnit, p.units)}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'sm',
      render: (p) => <ActiveChip active={p.isActive} />,
    },
    {
      key: 'price',
      header: 'Sale price',
      align: 'right',
      render: (p) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }} component="div">
          <PriceRangeText variants={p.variants} />
          <Typography variant="caption" color="text.secondary" component="div">
            per {p.baseUnit.symbol}
          </Typography>
        </Typography>
      ),
    },
  ];

  const canCreate = can('products.create');
  const filtered = q !== '' || categoryId !== '' || brandId !== '' || active !== 'true';

  return (
    <>
      <PageHeader
        title="Products"
        subtitle="Everything you buy and sell, with variants, prices and pack sizes."
        actions={
          <>
            <Tooltip title="Find by barcode or SKU">
              <IconButton
                aria-label="Find by barcode"
                onClick={() => setScanning(true)}
                sx={{ border: 1, borderColor: 'divider' }}
              >
                <QrCodeScannerRounded />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<TuneRounded />}
              onClick={() => void navigate('/products/setup')}
            >
              Setup
            </Button>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddRounded />}
                onClick={() => void navigate('/products/new')}
              >
                New product
              </Button>
            )}
          </>
        }
      />
      <>
        <DataTable
          {...exportSelection.table}
          columns={[...columns, ...filesColumn<Product>(fileCounts)]}
          rows={list.data?.data}
          rowKey={(p) => p.id}
          loading={list.isLoading}
          page={list.data?.page}
          onPageChange={(p) => update({ page: String(p) })}
          onPageSizeChange={(s) => update({ pageSize: String(s), page: null })}
          onRowClick={(p) => void navigate(`/products/${p.id}`)}
          toolbar={
            <>
              <SearchField
                value={q}
                onChange={(v) => update({ q: v, page: null })}
                placeholder="Search name, code, SKU or barcode"
              />
              <CategorySelect
                label="Category"
                size="small"
                categories={categories.data}
                noneLabel="All categories"
                value={categoryId}
                onChange={(e) => update({ category: e.target.value, page: null })}
                sx={{ minWidth: { sm: 170 } }}
              />
              <TextField
                select
                size="small"
                label="Brand"
                slotProps={EMPTY_SELECT_SLOTS}
                value={brandId}
                onChange={(e) => update({ brand: e.target.value, page: null })}
                sx={{ minWidth: { sm: 150 } }}
              >
                <MenuItem value="">
                  <Typography color="text.secondary">All brands</Typography>
                </MenuItem>
                {(brands.data ?? [])
                  .filter((b) => b.kind === 'BRAND')
                  .map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
              </TextField>
              <Box sx={{ flex: 1 }} />
              <TextField
                select
                size="small"
                label="Sort"
                value={sort}
                onChange={(e) =>
                  update({ sort: e.target.value === 'name' ? null : e.target.value, page: null })
                }
                sx={{ minWidth: { sm: 130 } }}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="code">Code</MenuItem>
                <MenuItem value="-createdAt">Newest</MenuItem>
              </TextField>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={active}
                onChange={(_, v: ActiveFilter | null) =>
                  v && update({ active: v === 'true' ? null : v, page: null })
                }
                aria-label="Status filter"
              >
                <ToggleButton value="true">Active</ToggleButton>
                <ToggleButton value="false">Inactive</ToggleButton>
                <ToggleButton value="all">All</ToggleButton>
              </ToggleButtonGroup>
            </>
          }
          empty={
            filtered ? (
              <EmptyState
                icon={<SearchOffRounded />}
                title="No products match"
                body="Try another name or code, or clear the filters."
                action={{
                  label: 'Clear filters',
                  onClick: () =>
                    update({ q: null, category: null, brand: null, active: null, page: null }),
                }}
              />
            ) : (
              <EmptyState
                icon={<CategoryRounded />}
                title="Your catalog is empty"
                body="Add the products you sell — with sizes, prices and barcodes — to start making sales and purchases."
                action={
                  canCreate
                    ? {
                        label: 'Add your first product',
                        onClick: () => void navigate('/products/new'),
                      }
                    : undefined
                }
              />
            )
          }
        />
        {exportSelection.dialog}
      </>
      <BarcodeLookupDialog open={scanning} onClose={() => setScanning(false)} />
    </>
  );
}
